import { updateUnitContent, uploadUnitAsset, useUnitOverride } from '../../services/unitOverridesService';
import { useTTS } from '../../hooks/useTTS';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import UnitPageShell from '../../components/shared/UnitPageShell';
import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Paper, Box, IconButton, Button } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
// removed unused theme import to avoid lint warnings
const PAGE_ID = 'm6_u2_x2_wilo_maxo';

const M6_U2_X2_WiloMaxo: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const textBlocks = useMemo(() => ([
    { key: 'intro', heading: '', body: 'Die Wilo Stratos MAXO ist eine intelligente Hocheffizienz-Nassläuferpumpe für Heizungs-, Kälte- und Klimaanwendungen in größeren Wohngebäuden, Gewerbe und Industrie. Sie bietet höchste Systemeffizienz durch optimierte und innovative Energiesparfunktionen.' },
    { key: 'merkmale', heading: 'Wesentliche Merkmale', body: '• Hohe Energieeffizienz: EC-Motortechnologie.\n• Intuitive Bedienung: Grüner Knopf, großes Farbdisplay, anwendungsgeführte Einstellungen (Setting Guide).\n• Vielfältige Regelungsarten: Δp-c, Δp-v, Δp-T (Sensor nötig), Q-const (Sensor/Berechnung), Drehzahlkonstant, PID-Regler, Multi-Flow Adaptation, No-Flow Stop, Dynamic Adapt plus.\n• Integrierte Wärmemengen- und Kältemengenzählung.' },
    { key: 'kommunikation', heading: 'Kommunikationsschnittstellen', body: 'Standardmäßig integriert:\n• Bluetooth (für Wilo-Assistant App)\n• Digitaleingänge (Ext. Aus, Ext. Min, etc.)\n• Analogsignal (0-10V, 2-10V, 0-20mA, 4-20mA)\n• Sammelstörmeldung (SSM), Sammelbetriebsmeldung (SBM)\n\nOptional über CIF-Module (Steckmodule):\n• BACnet MS/TP, BACnet/IP\n• Modbus RTU, Modbus TCP\n• LON, CANopen, PLR\n• Profibus DP, Profinet IO' },
    { key: 'anschluss', heading: 'Anschluss der Stratos MAXO', body: 'Elektrisch:\n• 1~230V oder 3~400V je nach Modell.\n• Wilo-Connector (größere Ausführung) oder Klemmkasten.\n• Steuersignale über separate Klemmen oder CIF-Modul.\n\nHydraulisch:\n• Gewinde- oder Flanschanschluss (DN 32 bis DN 100).\n• Einbaulage: Motorwelle horizontal.' },
    { key: 'wichtig', heading: 'Worauf beim Einsatz achten?', body: '• Regelungsart entsprechend der Anwendung wählen (Heizung, Kühlung, Volumenstrom).\n• Bei BUS-Anbindung: Passende CIF-Module verwenden und korrekt parametrieren.\n• Display-Einstellungen für Anzeige von Wärmemenge / Kältemenge aktivieren, falls gewünscht.\n• Für Mehrpumpensysteme: Multi-Flow Adaptation nutzen.' }
  ]), []);

  const images = [
    { key: 'img_maxo_display', src: '/assets/drawable/pumpe_maxo_serie.png', label: 'Wilo Stratos MAXO mit Farbdisplay' },
    { key: 'img_maxo_schnittstellen', src: '/assets/drawable/pumpe_maxo_display.png', label: 'Anschlüsse und Schnittstellen der Stratos MAXO' },
    { key: 'img_maxo_anwendung', src: '/assets/drawable/pumpe_maxo_anschluss.png', label: 'Anwendungsbeispiel Wilo Stratos MAXO in einer Anlage' }
  ];

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = useMemo(
    () => textBlocks.map(tb => ({ ...tb, body: override?.texts?.[tb.key] || tb.body })),
    [override, textBlocks]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullText = useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = useCallback(async (blockKey: string, newBody: string) => {
    if (!token) return;
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockKey]: newBody } }, token);
      alert('Gespeichert.');
      await refresh();
    } catch (e) {
      console.error(e);
      alert('Fehler beim Speichern.');
    }
  }, [token, refresh]);

  const handleImageReplace = useCallback(async (imageKey: string, file: File) => {
    if (!token) return;
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild aktualisiert.');
      await refresh();
    } catch (e) {
      console.error(e);
      alert('Fehler beim Upload.');
    }
  }, [token, refresh]);

  const ttsBtn = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    color: 'primary',
    sx: { ml: 1 }
  }, React.createElement(VolumeUpIcon));

  return React.createElement(UnitPageShell, {
    title: '6.2.2 Wilo Stratos MAXO',
    actions: ttsBtn
  }, React.createElement(Paper, { elevation: 0, sx: { backgroundColor: 'transparent' } },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, {
          key: img.key,
          src: imgSrc(img.key, img.src),
          label: img.label,
          onReplace: canUploadImages ? (file) => handleImageReplace(img.key, file) : undefined
        }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 3 } },
        mergedBlocks.map(block => React.createElement(EditableTextBlock, {
          key: block.key,
          pageId: PAGE_ID,
          blockId: block.key,
          heading: block.heading,
          body: block.body,
          onSave: canEditTexts ? (body: string) => handleTextSave(block.key, body) : undefined
        }))
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
      React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m6_u2_x99_quiz') }, 'Zum Quiz')
    )
  ));
};

export default M6_U2_X2_WiloMaxo;


